/*
 * The admin console entry point. Same three providers in the same order as the recruiter
 * app, for the same reasons:
 *
 *   I18nProvider     outermost, because everything below it renders copy — including the error
 *                    states of the two providers underneath.
 *   QueryClient      the single data-fetching idiom (technology-selection §9).
 *   SessionProvider  innermost of the three, because it renders copy and issues queries.
 *
 * The stylesheet is imported here and only here. A component importing it would make styling a
 * side effect of whichever module happened to load first.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nProvider } from '@indovation/i18n/react';
import '@indovation/design-system/styles.css';
import { App } from './app.js';
import { SessionProvider } from './auth/session.js';
import { Toasts } from './app/toasts.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 or 403 will not become a 200 by asking again, and retrying an expired credential
      // three times just delays the redirect to sign-in.
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status;
        if (status === 401 || status === 403 || status === 404) {
          return false;
        }
        return failureCount < 2;
      },
      staleTime: 30_000,
    },
  },
});

const container = document.getElementById('root');
if (!container) {
  throw new Error('index.html has no #root element.');
}

createRoot(container).render(
  <StrictMode>
    <I18nProvider>
      <Toasts>
        <QueryClientProvider client={queryClient}>
          <SessionProvider>
            <App />
          </SessionProvider>
        </QueryClientProvider>
      </Toasts>
    </I18nProvider>
  </StrictMode>,
);
